import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { initLenis, destroyLenis, scrollToTop } from "@/lib/lenis";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import HowWeHelp from "@/pages/HowWeHelp";
import GrowVise90 from "@/pages/GrowVise90";
import MarketEntry from "@/pages/MarketEntry";
import HealthCheck from "@/pages/HealthCheck";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudy from "@/pages/CaseStudy";
import About from "@/pages/About";
import Booking from "@/pages/Booking";
import Privacy from "@/pages/Privacy";
import SiteAudit from "@/pages/SiteAudit";
import NotFound from "@/pages/NotFound";
import { SHOW_RESULTS, CASE_STUDIES } from "@/data/content";

// Every real page. scripts/prerender.mjs renders each of these to static HTML at build time.
export const PAGE_ROUTES = [
    { path: "/", element: <Home /> },
    { path: "/how-we-help", element: <HowWeHelp /> },
    { path: "/growvise-90", element: <GrowVise90 /> },
    { path: "/market-entry-distribution", element: <MarketEntry /> },
    { path: "/business-health-check", element: <HealthCheck /> },
    ...(SHOW_RESULTS
        ? [
              { path: "/our-results", element: <CaseStudies /> },
              ...CASE_STUDIES.map((c) => ({ path: `/our-results/${c.slug}`, element: <CaseStudy c={c} /> })),
          ]
        : []),
    { path: "/about", element: <About /> },
    { path: "/book-a-conversation", element: <Booking /> },
    { path: "/privacy", element: <Privacy /> },
    { path: "/test-your-website", element: <SiteAudit /> },
];

const ScrollManager = () => {
    const { pathname } = useLocation();
    useEffect(() => scrollToTop(), [pathname]);
    return null;
};

// Shared by the browser app and the build-time prerender.
export function AppShell() {
    return (
        <div className="min-h-screen bg-navy-950 text-slate-200">
            <Navbar />
            <main>
                <Routes>
                    {PAGE_ROUTES.map((r) => (
                        <Route key={r.path} path={r.path} element={r.element} />
                    ))}
                    <Route path="/commercial-growth" element={<Navigate to="/how-we-help" replace />} />
                    <Route path="/growth-assessment" element={<Navigate to="/business-health-check" replace />} />
                    <Route path="/case-studies" element={<Navigate to="/our-results" replace />} />
                    <Route path="/contact" element={<Navigate to="/book-a-conversation" replace />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

function App() {
    useEffect(() => {
        initLenis();
        return () => destroyLenis();
    }, []);

    return (
        <HelmetProvider>
            <BrowserRouter>
                <ScrollManager />
                <AppShell />
                <Toaster theme="dark" position="top-center" richColors />
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
